/**
 * Requests and renders Voyado ADD_TO_CART_RECS after Shopify confirms an item
 * was added. Shopify supplies the card markup; Voyado controls order and
 * tracking tickets.
 */
(function initializeVoyadoAddToCartPopup(window, document) {
    'use strict';

    const context = window.VOYADO_CONTEXT?.addToCartPopup;
    const root = document.querySelector('[data-voyado-add-to-cart-popup]');
    const productsContainer = root?.querySelector('[data-voyado-add-to-cart-products]');
    const sliderTemplate = root?.querySelector('[data-voyado-add-to-cart-slider-template]');
    const loading = root?.querySelector('[data-voyado-add-to-cart-loading]');
    const title = root?.querySelector('[data-voyado-add-to-cart-title]');
    const cardLoader = window.VOYADO_PRODUCT_CARD_LOADER;

    if (
        context?.enabled !== true ||
        !root ||
        !productsContainer ||
        !sliderTemplate ||
        !loading ||
        !title ||
        !window.VOYADO_ELEVATE?.createClient ||
        !cardLoader
    ) return;

    const client = window.VOYADO_ELEVATE.createClient();
    const debug = window.VOYADO_ELEVATE.debug || (() => {});
    let requestController = null;
    let requestId = 0;

    function normalizeVariantKey(value) {
        return window.VOYADO_ELEVATE.normalizeVariantKey(
            value,
            context.variantKeySource
        );
    }

    function itemKey(item) {
        const variantId = String(item?.variant_id ?? item?.id ?? '').trim();
        const sku = normalizeVariantKey(item?.sku);
        return context.variantKeySource === 'sku' ? (sku || variantId) : (variantId || sku);
    }

    async function getCartKeys(signal) {
        try {
            const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                signal,
            });
            if (!response.ok) return [];
            const cart = await response.json();
            return [...new Set((cart.items || []).map(itemKey).filter(Boolean))];
        } catch {
            return [];
        }
    }

    function addedVariantKey(detail) {
        const variantId = String(detail?.variantId || '').trim();
        const sku = normalizeVariantKey(detail?.sku);
        return context.variantKeySource === 'sku' ? (sku || variantId) : (variantId || sku);
    }

    function firstVisibleList(response) {
        return (response?.recommendationLists || []).find((list) => (
            list?.visible !== false && Array.isArray(list?.productGroups) && list.productGroups.length > 0
        ));
    }

    function closeCompetingOverlays() {
        document.querySelectorAll('.modal.is-open, .drawer.is-open').forEach((element) => {
            if (element !== root) element.classList.remove('is-open');
        });
    }

    function openPopup() {
        window.removeTrapFocus?.();
        closeCompetingOverlays();
        if (window.Woolman?.ModalsAndDrawers?.showModalOrDrawer) {
            window.Woolman.ModalsAndDrawers.showModalOrDrawer(root.id);
            return;
        }
        root.classList.add('initialized', 'is-open');
        document.body.classList.add('overflow-hidden');
    }

    function closePopup() {
        window.removeTrapFocus?.();
        if (window.Woolman?.ModalsAndDrawers?.closeModalOrDrawer) {
            window.Woolman.ModalsAndDrawers.closeModalOrDrawer(root.id);
            return;
        }
        root.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
    }

    async function showRecommendations(detail) {
        const variantKey = addedVariantKey(detail);
        if (!variantKey) return;

        requestController?.abort();
        requestController = new AbortController();
        const controller = requestController;
        const activeRequest = ++requestId;
        productsContainer.replaceChildren();
        loading.hidden = false;
        root.setAttribute('aria-busy', 'true');
        root.hidden = false;
        openPopup();

        try {
            const cart = await getCartKeys(controller.signal);
            if (activeRequest !== requestId || controller.signal.aborted) return;

            const response = await client.addToCartPopup(
                { variantKey, cart, channels: 'ONLINE' },
                {
                    recommendationLists: [{
                        id: context.listId || 'add-to-cart-popup',
                        algorithm: 'ADD_TO_CART_RECS',
                        label: context.label || 'Recommended for you',
                        limit: Math.max(1, Number(context.limit) || 10),
                        visualization: 'CAROUSEL',
                    }],
                },
                { signal: controller.signal }
            );

            if (activeRequest !== requestId || controller.signal.aborted) return;
            const list = firstVisibleList(response);
            if (!list) {
                closePopup();
                return;
            }

            const products = list.productGroups.map((group) => group?.products?.[0]).filter(Boolean);
            const cards = (await Promise.all(products.map((product) => cardLoader.load(product)))).filter(Boolean);
            if (activeRequest !== requestId || !root.isConnected) return;
            if (!cards.length) {
                closePopup();
                return;
            }

            title.textContent = list.label || context.label || '';
            const slider = sliderTemplate.content.cloneNode(true);
            const track = slider.querySelector('[data-voyado-add-to-cart-track]');
            const arrows = slider.querySelector('.gridy-arrows');
            if (!track) throw new Error('Add-to-cart recommendation track is missing.');
            track.replaceChildren(...cards);
            arrows?.classList.toggle('md-hide', cards.length <= 4);
            arrows?.classList.toggle('sm-to-md-hide', cards.length <= 3);
            arrows?.classList.toggle('sm-down-hide', cards.length <= 1);
            productsContainer.replaceChildren(slider);
            loading.hidden = true;
            root.removeAttribute('aria-busy');
        } catch (error) {
            if (controller.signal.aborted || activeRequest !== requestId) return;
            loading.hidden = true;
            root.removeAttribute('aria-busy');
            closePopup();
            debug('Add-to-cart popup recommendations skipped', {
                message: error?.message || String(error),
            });
        }
    }

    document.addEventListener('ajaxProduct:added', (event) => showRecommendations(event.detail));
})(window, document);
